// Chat UI Module - UI 상태 관리 및 렌더링 로직
// 메인 chat_controller.js에서 import하여 사용

import { getI18nMessage } from '../../lib/i18n_helper'
import { motionMs, prefersReducedMotion } from '../../lib/motion'

// Within this distance of the bottom, a new message glides into view instead of jumping.
const NEAR_BOTTOM_PX = 160

export const UIMixin = {
  // 메시지 DOM 추가
  appendMessage(data) {
    try {
      // 하단 앵커 래퍼(mt-auto)에 삽입 → 버블이 바닥부터 쌓임. 없으면 외곽 스크롤러로 폴백.
      const container = this.hasMessagesInnerTarget ? this.messagesInnerTarget : this.messagesTarget

      // ⭐ 중복 메시지 방지 (같은 ID의 메시지가 이미 있으면 무시)
      const existingMessage = this.messagesTarget.querySelector(`article[data-message-id="${data.id}"]`)
      if (existingMessage) {
        console.debug('[Chat] Duplicate message ignored:', data.id)
        return
      }

      // Empty State 제거 (첫 메시지 추가 시)
      const emptyState = this.messagesTarget.querySelector('#chat-empty-state')
      if (emptyState) {
        emptyState.style.transition = `opacity ${motionMs('QUICK_MS')}ms var(--ease-exit)`
        emptyState.style.opacity = '0'
        setTimeout(() => emptyState.remove(), motionMs('QUICK_MS'))
      }

      const isOwnMessage = data.user_id === this.currentUserIdValue
      const currentTimestamp = Date.parse(data.created_at)
      let showTimestamp = true

      if (!Number.isNaN(currentTimestamp)) {
        if (this.lastMessageTimestamp !== null) {
          const timeDiff = currentTimestamp - this.lastMessageTimestamp
          const interval = window.BusinessConstants?.MESSAGE_TIMESTAMP_INTERVAL_MS || 300000
          showTimestamp = timeDiff >= interval
        }

        // 다음 메시지 비교를 위해 마지막 타임스탬프 갱신
        this.lastMessageTimestamp = currentTimestamp
      }

      const scroller = this.messagesTarget
      this.glideToLatest = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight < NEAR_BOTTOM_PX

      const messageHtml = this.createMessageHTML(data, isOwnMessage, showTimestamp)
      container.insertAdjacentHTML("beforeend", messageHtml)

      // 추가된 메시지: 말풍선이 꼬리 쪽에서 부풀며 올라옴 (id로 선택 — 마지막 자식은 예약카드일 수 있음)
      const addedMessage = container.querySelector(`article[data-message-id="${data.id}"]`)
      addedMessage?.classList.add('message-in', isOwnMessage ? 'message-in--own' : 'message-in--peer')
    } catch (error) {
      console.error('[Chat] appendMessage failed:', error, 'data:', data)
      // 에러 발생해도 기존 메시지는 유지됨
    }
  },

  // 메시지 HTML 생성
  createMessageHTML(data, isOwnMessage, showTimestamp = true) {
    const isoTimestamp = data.created_at || new Date().toISOString()
    const displayTimestamp = new Date(data.created_at).toLocaleString('zh-TW', {
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })

    const alignmentClass = isOwnMessage ? 'justify-end' : 'justify-start'
    const orderClass = isOwnMessage ? 'order-1' : 'order-2'

    // ⭐ Optional chaining + fallback for bubble classes
    const defaultSentClass = 'rounded-2xl rounded-tr-sm bg-crust-600 text-white'
    const defaultReceivedClass = 'rounded-2xl rounded-tl-sm bg-white text-charcoal-800 shadow-sm border border-charcoal-100'
    const bubbleClass = isOwnMessage
      ? (window.BusinessConstants?.MESSAGE_BUBBLE_CLASSES?.sent || defaultSentClass)
      : (window.BusinessConstants?.MESSAGE_BUBBLE_CLASSES?.received || defaultReceivedClass)
    const timeClass = isOwnMessage ? 'text-right' : 'text-left'

    // Extract function calls for readability and testability
    const readIndicatorLabel = getI18nMessage(this.element, 'messageReadIndicator', '已讀')
    const escapedContent = this.escapeHtml(data.content || '')

    // Read indicator SVG (조건부 렌더링)
    const readIndicatorHTML = (isOwnMessage && data.read) ? `
      <svg class="inline-block ml-1 h-3.5 w-3.5 text-crust-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-label="${readIndicatorLabel}">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"/>
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M9 13l4 4L23 7" opacity="0.7"/>
      </svg>
    ` : ''

    // 타임스탬프 HTML (조건부 렌더링)
    const timestampHTML = showTimestamp ? `
      <div class="mt-1 px-2 ${timeClass}">
        <time class="text-xs text-charcoal-500" datetime="${isoTimestamp}">${displayTimestamp}</time>
        ${readIndicatorHTML}
      </div>
    ` : ''

    return `
      <article class="flex ${alignmentClass}" data-message-id="${data.id}" data-message-timestamp="${isoTimestamp}">
        <div class="max-w-[75%] ${orderClass}">
          <div class="${bubbleClass} px-4 py-2.5">
            <p class="text-sm leading-relaxed whitespace-pre-wrap break-words">${escapedContent}</p>
          </div>
          ${timestampHTML}
        </div>
      </article>
    `
  },

  // 스크롤을 맨 아래로
  scrollToBottom() {
    requestAnimationFrame(() => {
      this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight
    })
  },

  // 새 메시지 뒤: 바닥 근처였다면 부드럽게, 아니면 즉시 맨 아래로
  scrollToLatest() {
    const smooth = this.glideToLatest && !prefersReducedMotion()
    requestAnimationFrame(() => {
      this.messagesTarget.scrollTo({ top: this.messagesTarget.scrollHeight, behavior: smooth ? 'smooth' : 'auto' })
    })
  },

  // 기존 렌더링된 메시지를 최신 버블 구조로 정규화
  normalizeExistingMessages() {
    const articles = this.messagesTarget.querySelectorAll('article[data-message-id]')

    articles.forEach((article) => {
      const container = article.firstElementChild
      if (container && container.classList.contains('max-w-[85%]')) {
        container.classList.remove('max-w-[85%]')
        container.classList.add('max-w-[75%]')
      }

      const bubble = container ? container.firstElementChild : null
      if (!bubble) return

      if (bubble.classList.contains('animate-scale-in')) {
        this.normalizeLegacyBubble(article, bubble)
      } else {
        // 최신 구조라도 패딩이 누락된 경우 보정
        if (!bubble.classList.contains('px-4')) {
          bubble.classList.add('px-4')
        }
        if (!bubble.classList.contains('py-2.5')) {
          bubble.classList.add('py-2.5')
        }
      }

      this.ensureArticleTimestamp(article)
    })
  },

  normalizeLegacyBubble(article, bubble) {
    const isOwnMessage = article.classList.contains('justify-end')

    // 내부 래퍼(div.px-4 py-2.5)를 bubble로 승격
    const contentWrapper = bubble.firstElementChild
    if (contentWrapper) {
      while (contentWrapper.firstChild) {
        bubble.appendChild(contentWrapper.firstChild)
      }
      contentWrapper.remove()
    }

    const bubbleClassFromConstants = window.BusinessConstants?.MESSAGE_BUBBLE_CLASSES?.[
      isOwnMessage ? 'sent' : 'received'
    ]

    if (bubbleClassFromConstants) {
      bubble.className = `${bubbleClassFromConstants} px-4 py-2.5`
    } else {
      bubble.className = [
        'rounded-2xl',
        isOwnMessage ? 'rounded-tr-sm' : 'rounded-tl-sm',
        isOwnMessage ? 'bg-crust-600' : 'bg-white',
        isOwnMessage ? 'text-white' : 'text-charcoal-800',
        !isOwnMessage ? 'shadow-sm' : '',
        !isOwnMessage ? 'border' : '',
        !isOwnMessage ? 'border-charcoal-100' : '',
        'px-4',
        'py-2.5'
      ].filter(Boolean).join(' ')
    }
  },

  ensureArticleTimestamp(article) {
    if (article.dataset.messageTimestamp) return

    const timeTag = article.querySelector('time')
    if (!timeTag) return

    const datetime = timeTag.getAttribute('datetime') || timeTag.dataset.timestamp
    if (datetime) {
      article.dataset.messageTimestamp = datetime
    }
  },

  initializeLastMessageTimestamp() {
    const articles = this.messagesTarget.querySelectorAll('article[data-message-id]')
    if (!articles.length) return

    const lastArticle = articles[articles.length - 1]
    const timestamp = lastArticle.dataset.messageTimestamp
    if (timestamp) {
      const parsed = Date.parse(timestamp)
      if (!Number.isNaN(parsed)) {
        this.lastMessageTimestamp = parsed
      }
    }
  },

  // Earlier messages: the button (or scrolling it into view) fetches the page before the oldest one shown and
  // prepends it without moving what the reader is looking at.
  observeOlder() {
    if (!this.hasOlderRowTarget || !("IntersectionObserver" in window)) return
    this.olderObserver = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) this.loadOlder()
    }, { root: this.messagesTarget, rootMargin: "200px 0px 0px 0px" })
    this.olderObserver.observe(this.olderRowTarget)
  },

  async loadOlder() {
    if (this.loadingOlder || !this.hasOlderButtonTarget) return
    this.loadingOlder = true
    const button = this.olderButtonTarget
    button.disabled = true

    try {
      const response = await fetch(`/chats/${this.chatIdValue}/older?before=${button.dataset.beforeId}`, {
        headers: { Accept: "text/html" }, credentials: "same-origin"
      })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)

      const html = await response.text()
      const container = this.messagesTarget
      const previousHeight = container.scrollHeight
      this.olderRowTarget.insertAdjacentHTML("afterend", html)
      container.scrollTop += container.scrollHeight - previousHeight
      this.normalizeExistingMessages()

      if (response.headers.get("X-Has-Older") === "true") {
        button.dataset.beforeId = response.headers.get("X-Oldest-Id")
        button.disabled = false
      } else {
        this.olderObserver?.disconnect()
        this.olderRowTarget.remove()
      }
    } catch (_error) {
      button.disabled = false
    } finally {
      this.loadingOlder = false
    }
  },

  // XSS 방지를 위한 HTML 이스케이프
  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  },

  // 읽음 표시 업데이트 (실시간 브로드캐스트 수신 시)
  handleReadReceipts(messageIds) {
    if (!messageIds || !messageIds.length) return

    const readIndicatorLabel = getI18nMessage(this.element, 'messageReadIndicator', '已讀')

    messageIds.forEach((messageId) => {
      const article = this.messagesTarget.querySelector(`article[data-message-id="${messageId}"]`)
      if (!article) return

      // 타임스탬프 영역 찾기
      const timeContainer = article.querySelector('time')
      if (!timeContainer || !timeContainer.parentElement) return

      // 이미 읽음 표시가 있으면 중복 추가 방지
      if (timeContainer.parentElement.querySelector('svg[aria-label]')) return

      // 읽음 표시 SVG 생성 및 추가
      const readIndicatorHTML = `
        <svg class="inline-block ml-1 h-3.5 w-3.5 text-crust-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-label="${readIndicatorLabel}">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"/>
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M9 13l4 4L23 7" opacity="0.7"/>
        </svg>
      `
      timeContainer.parentElement.insertAdjacentHTML('beforeend', readIndicatorHTML)

      // 추가된 SVG에 페이드인 애니메이션 적용
      const addedSvg = timeContainer.parentElement.querySelector('svg[aria-label]:last-child')
      if (addedSvg) {
        addedSvg.style.opacity = '0'
        requestAnimationFrame(() => {
          addedSvg.style.transition = 'opacity 0.3s ease-in'
          addedSvg.style.opacity = '1'
        })
      }
    })
  }
}
